import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanDeactivate,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

type NavigationDecision = () => boolean | Promise<boolean>;

interface NavigationGrant {
  targetUrl: string;
  navigationId: number | null;
  /**
   * A widget can obtain confirmation before Router emits NavigationStart. In
   * that case the same grant must cover both route guards; a guard-originated
   * confirmation only needs one reuse for the other guard in the pair.
   */
  remainingChecks: number;
}

/**
 * The appointment-type forms editor is created dynamically inside the shared
 * page host, so Angular cannot inject that child into a typed CanDeactivate
 * guard. This registry is separate from the email-template guard: one decision
 * slot, and only the forms page may register it.
 */
@Injectable({ providedIn: 'root' })
export class AppointmentTypeFormsNavigationState {
  private decision: NavigationDecision | undefined;
  private registrationToken: object | undefined;
  private permittedGrant: NavigationGrant | undefined;
  private decisionSequence = 0;
  private currentNavigationId: number | null = null;
  private currentNavigationUrl = '';

  register(decision: NavigationDecision): () => void {
    const token = {};
    this.registrationToken = token;
    this.decision = decision;

    return () => {
      if (this.registrationToken !== token) return;
      this.registrationToken = undefined;
      this.decision = undefined;
      this.clearNavigationGrant();
    };
  }

  beginNavigation(navigationId: number, targetUrl: string): void {
    this.currentNavigationId = navigationId;
    this.currentNavigationUrl = this.targetUrl(targetUrl);
    this.decisionSequence += 1;
    if (!this.permittedGrant) return;
    if (this.permittedGrant.navigationId === null && this.permittedGrant.targetUrl === this.currentNavigationUrl) {
      // A widget can obtain confirmation before Router emits NavigationStart.
      // Bind that one grant to the exact attempt once the event arrives.
      this.permittedGrant.navigationId = navigationId;
      return;
    }
    if (this.permittedGrant.navigationId !== navigationId || this.permittedGrant.targetUrl !== this.currentNavigationUrl) {
      this.permittedGrant = undefined;
    }
  }

  clearNavigationGrant(): void {
    this.permittedGrant = undefined;
    this.currentNavigationId = null;
    this.currentNavigationUrl = '';
    this.decisionSequence += 1;
  }

  async confirmNavigation(targetUrl: string, navigationId?: number): Promise<boolean> {
    const target = this.targetUrl(targetUrl);
    const attempt = navigationId ?? this.currentNavigationId;
    if (this.permittedGrant
      && this.permittedGrant.targetUrl === target
      && (this.permittedGrant.navigationId === null
        ? true
        : attempt !== null && this.permittedGrant.navigationId === attempt)) {
      // CanDeactivate and CanActivate can both run for one route transition.
      // A pre-router widget confirmation has two guard checks remaining; a
      // grant created by the first guard has one reuse remaining. A terminal
      // router event clears any grant that was not reused.
      if (this.permittedGrant.remainingChecks > 1) this.permittedGrant.remainingChecks -= 1;
      else this.permittedGrant = undefined;
      return true;
    }

    // A previous confirmation must never authorize another exact URL or
    // another router attempt.
    this.permittedGrant = undefined;
    if (!this.decision) return true;

    const sequence = ++this.decisionSequence;
    const allowed = await this.decision();
    if (sequence !== this.decisionSequence) return false;
    if (allowed) {
      this.permittedGrant = {
        targetUrl: target,
        navigationId: attempt,
        remainingChecks: attempt === null ? 2 : 1,
      };
    }
    return allowed;
  }

  private targetUrl(url: string): string {
    return url || '/';
  }
}

@Injectable({ providedIn: 'root' })
export class AppointmentTypeFormsNavigationGuard implements CanActivate, CanDeactivate<unknown> {
  constructor(
    private readonly state: AppointmentTypeFormsNavigationState,
    private readonly router: Router,
  ) {
    const routerEvents = this.router.events;
    if (!routerEvents) return;

    routerEvents.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.state.beginNavigation(event.id, event.url);
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError || event instanceof NavigationSkipped) {
        this.state.clearNavigationGrant();
      }
    });
  }

  /**
   * The shared dynamic page stays alive when only :pageId changes, so
   * canDeactivate does not run for a tab change. canActivate does, because
   * those routes set runGuardsAndResolvers: 'always'.
   */
  canActivate(_route: ActivatedRouteSnapshot, routerState: RouterStateSnapshot): boolean | Promise<boolean> {
    if (!this.isWorkspacePath(this.router.url)) return true;
    if (this.path(this.router.url) === this.path(routerState.url)) return true;
    return this.state.confirmNavigation(routerState.url, this.navigationId());
  }

  canDeactivate(
    _component: unknown,
    _currentRoute: ActivatedRouteSnapshot,
    _currentState: RouterStateSnapshot,
    nextState?: RouterStateSnapshot,
  ): boolean | Promise<boolean> {
    return this.state.confirmNavigation(nextState?.url ?? '/', this.navigationId());
  }

  private isWorkspacePath(url: string): boolean {
    const path = this.path(url);
    return path === '/apps/appointments/pages/appointment-type-forms'
      || path === '/appointments/appointment-type-forms';
  }

  private path(url: string): string {
    return (url || '').split(/[?#]/, 1)[0].replace(/\/+$/, '') || '/';
  }

  private navigationId(): number | undefined {
    const navigation = this.router.getCurrentNavigation?.();
    return navigation && typeof navigation.id === 'number' ? navigation.id : undefined;
  }
}
